/**
 * Reports feature types (P3-T9) — mirrored from the issue-reports service
 * response views (`services/issue-reports/src/reports/`), which return plain
 * JSON (no `{data}` envelope; `reportsApi.unwrap` only strips a genuine
 * single-key envelope, so these payloads — including the cursor page's own
 * `data` key — pass through intact).
 *
 * Design doc: plans/designs/workspace-platform-p3-ui-design.md (§10, §12).
 */

/** Resource-type discriminator used on projects-service attachment rows (P3-T7). */
export const REPORT_PROJECT_RESOURCE_TYPE = 'report-project';

/** `ReportProjectView` (report-projects.service.ts). */
export interface ReportProjectView {
  id: string;
  name: string;
  /** FK to the projects-service Project; set on create from the panel (A1). */
  projectId?: string;
  ownerUserId: string;
  settings: Record<string, unknown>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

/** `ReportSummaryView` — panel counts (`GET /v1/reports/projects/:id/summary`). */
export interface ReportSummaryView {
  id: string;
  environments: number;
  reports: number;
  /** Active primaries — issues dedup answers `reject-known` for ("known issues"). */
  activeKnownIssues: number;
}

export type ReportStatus = 'active' | 'resolved';

/**
 * `ReportResourceView` — refs only, never file content (P5 immutable).
 * `downloadUrl` is a server-minted presign; absent → "Download unavailable".
 */
export interface ReportResourceView {
  service: string;
  resourceId: string;
  filename?: string;
  size?: number;
  downloadUrl?: string;
}

/** `ReportSearchRow` — one search page row (T7's pinned shape). */
export interface ReportSearchRow {
  id: string;
  status: string;
  finalizedAt?: string;
  reason: string;
  debouncedCounter: number;
  duplicateOf?: string;
  createdAt?: string;
  resources: ReportResourceView[];
}

/** `CursorPaginationInfo` (@cluster/common) — next-cursor field is `cursor`, never `nextCursor` (Q3). */
export interface CursorPaginationInfo {
  cursor: string | null;
  hasMore: boolean;
  totalCount?: number;
}

export interface ReportSearchPage {
  data: ReportSearchRow[];
  pagination: CursorPaginationInfo;
}

/** `EnvironmentView` — prefix only; the plaintext key never appears here (P6). */
export interface EnvironmentView {
  id: string;
  reportProjectId: string;
  name: string;
  status: string;
  duplicateThreshold: number;
  maxResources: Record<string, number>;
  retentionDays?: number;
  apiKeyPrefix?: string;
  ingestPolicyId?: string;
  createdAt: string;
  updatedAt: string;
}

/** Create/rotate response (`EnvironmentsService`): `{environment, apiKey}` — `apiKey` is Q2/A2, shown once. */
export interface ProvisionedEnvironment {
  environment: EnvironmentView;
  apiKey: string;
}

/**
 * The subset of a projects-service resource row the Sub-projects section needs.
 * Typed `type: string` (not the projects feature's 8-value union, which
 * predates `report-project` and is off-limits to this task — see the P3-T9
 * host-integration blocker).
 */
export interface SubProjectResourceRow {
  _id: string;
  projectId: string;
  type: string;
  refId: string;
  name?: string;
  addedAt?: string;
  createdAt?: string;
}

/**
 * Permission gates (§11.1) — the exact `@RequirePolicy(action, resource)`
 * strings on the reports controllers. Reports-domain resources are
 * CLASS-scoped, so `can()` probes those literals, not instance ids.
 */
export const REPORTS_PERMISSIONS = {
  projectCreate: { action: 'reports:projects:create', resource: 'reports:projects' },
  environmentCreate: { action: 'reports:environments:create', resource: 'reports:environments' },
  environmentRotateKey: {
    action: 'reports:environments:rotate-key',
    resource: 'reports:environments',
  },
} as const;

/** Webhook path echoed in the key modal (D4: a key without its endpoint is unusable). */
export const webhookPath = (environmentId: string): string =>
  `/v1/reports/environments/${environmentId}/reports`;
